import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { toast } from 'react-hot-toast';
import { supabase } from '../../config/supabase';
import SidePanel from '../common/SidePanel';
import { Link } from 'react-router-dom';
import Logo from '../Logo';

export default function CompleteProfile() {
  const [formData, setFormData] = useState({
    phoneNumber: '',
    country: '',
    companyName: '',
    businessWebsite: '',
  });
  const [countries, setCountries] = useState([]);
  const navigate = useNavigate();
  const { updateProfile } = useAuth();

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await fetch('https://restcountries.com/v3.1/all');
        const data = await response.json();
        const countryList = data.map(country => ({
          value: country.cca3, // Use the 3-letter country code
          label: country.name.common,
        }));
        setCountries(countryList);
      } catch (error) {
        console.error('Error fetching countries:', error);
      }
    };

    fetchCountries();
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error) {
        console.error('Error fetching user:', error);
        toast.error('Failed to fetch user data');
      }
      // You can use the user data here if needed
    };

    fetchUser();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error) throw error;

      await updateProfile({
        id: user.id,
        phone_number: formData.phoneNumber,
        country: formData.country,
        company_name: formData.companyName,
        business_website: formData.businessWebsite,
      });

      toast.success('Profile updated successfully!');
      navigate('/onboarding/markets'); // Redirect to the onboarding page
    } catch (err) {
      console.error('Error updating profile:', err);
      toast.error('Failed to update profile');
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Hide SidePanel on mobile */}
      <div className="hidden md:block">
        <SidePanel 
          title="Complete Your Profile"
          subtitle="Fill in the details to get started"
          steps={[
            { id: 'profile', title: 'Profile', description: 'Complete your profile information' }
          ]}
          currentStep="profile"
          completedSteps={[]}
        />
      </div>

      {/* Main Content - Adjust padding for mobile */}
      <div className="flex-1 md:pl-[400px] flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full space-y-8 p-4 md:p-0">
          {/* Add Logo */}
          <div className="flex justify-center">
            <Link to="/">
              <Logo className="h-12 w-auto" />
            </Link>
          </div>
          
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Please complete your profile
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              {/* Additional instructions or information can go here */}
            </p>
          </div>
          <form onSubmit={handleSubmit} className="mt-8 space-y-6">
            <div className="rounded-md shadow-sm -space-y-px">
              <div>
                <label htmlFor="phoneNumber" className="sr-only">
                  Phone Number
                </label>
                <input
                  type="text"
                  name="phoneNumber"
                  id="phoneNumber"
                  required
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                  placeholder="Phone Number"
                />
              </div>
              <div>
                <label htmlFor="country" className="sr-only">
                  Country
                </label>
                <select
                  name="country"
                  id="country"
                  required
                  value={formData.country}
                  onChange={handleChange}
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                >
                  <option value="" disabled>Select your country</option>
                  {countries.map(country => (
                    <option key={country.value} value={country.value}>
                      {country.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="companyName" className="sr-only">
                  Company Name
                </label>
                <input
                  type="text"
                  name="companyName"
                  id="companyName"
                  required
                  value={formData.companyName}
                  onChange={handleChange}
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                  placeholder="Company Name"
                />
              </div>
              <div>
                <label htmlFor="businessWebsite" className="sr-only">
                  Business Website
                </label>
                <input
                  type="url"
                  name="businessWebsite"
                  id="businessWebsite"
                  required
                  value={formData.businessWebsite}
                  onChange={handleChange}
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                  placeholder="Business Website (e.g., https://example.com)"
                />
              </div>
            </div>
            <div>
              <button
                type="submit"
                className="flex w-full justify-center rounded-md border border-transparent bg-[#0049ac] py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-[#0049ac]/90 focus:outline-none focus:ring-2 focus:ring-[#0049ac] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Complete Profile
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
} 